/* eslint-disable */

import { Text, Button } from "@mantine/core";
import React, { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Bell,
  BellOff,
  Calendar,
  Check,
  Message,
  User,
  Volume,
  VolumeOff,
  X,
} from "tabler-icons-react";
import {
  ActionIcon,
  Avatar,
  Badge,
  Divider,
  Group,
  Indicator,
  Menu,
  Paper,
  ScrollArea,
  Stack,
  Title,
  Tooltip,
  Center,
  UnstyledButton,
} from "@mantine/core";
import { useGetUser } from "api/users/getUser";
import { useSocket } from "hooks/useSocket";
import { showNotification } from "@mantine/notifications";
import { Navigate, useNavigate } from "react-router-dom";

interface NotificationInterface {
  id: number;
  type: "task_assigned" | "status_change" | "comment" | "deadline";
  title: string;
  message: string;
  timestamp: string | Date;
  read: boolean;
  priority: "low" | "medium" | "high" | "urgent";
  project: string;
  avatar: string | null;
  color: string;
  issueId: string;
  projectId: string;
}

function Notifications() {
  const { data: user } = useGetUser();
  const socket = useSocket(user?._id);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationInterface[]>(
    []
  );
  const [opened1, setOpened1] = useState(false);
  const [filter, setFilter] = useState("all");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [newNotificationCount, setNewNotificationCount] = useState(0);
  const audioRef = useRef(
    new Audio(
      "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYf"
    )
  );
  useEffect(() => {
    if (!socket) return;
    console.log("Socket in notifications:", socket);
    const handleNotification = (data: NotificationInterface) => {
      console.log("Received notification:", data);
      setNotifications((prev) => [data, ...prev]);
      if (soundEnabled) {
        audioRef.current.play().catch(() => {});
      }
      showNotification({
        title: data.title,
        message: `${data.message}`,
      });
    };
    socket.onAny((event: any, ...args) => {
      console.log(`📨 Received event: "${event}"`, ...args);
    });
    socket.on("notification", handleNotification);

    // return () => {
    //   socket.off("notification", handleNotification);
    // };
  }, [socket, soundEnabled]);
  useEffect(() => {
    if (Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const getNotificationIcon = (type: string, color: string) => {
    const iconProps = { size: 16 };
    switch (type) {
      case "task_assigned":
        return <User {...iconProps} />;
      case "status_change":
        return <Check {...iconProps} />;
      case "comment":
        return <Message {...iconProps} />;
      case "deadline":
        return <Calendar {...iconProps} />;
      default:
        return <AlertCircle {...iconProps} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "red";
      case "high":
        return "orange";
      case "medium":
        return "yellow";
      case "low":
        return "green";
      default:
        return "gray";
    }
  };

  const getTimeAgo = (timestamp: any) => {
    const now: any = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return "Just now";
  };

  const markAsRead = (id: any) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setNewNotificationCount(0);
  };

  const removeNotification = (id: any) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filter === "unread") return !n.read;
    if (filter === "urgent") return n.priority === "urgent";
    return true;
  });

  const handleToggle = () => {
    setOpened1(!opened1);
    if (!opened1) {
      setNewNotificationCount(0);
    }
  };

  return (
    <Menu
      opened={opened1}
      onChange={setOpened1}
      position="bottom-end"
      offset={5}
      width={400}
      shadow="xl"
      radius="md"
    >
      <Menu.Target>
        <Tooltip label="Notifications" position="bottom">
          <Indicator
            inline
            label={unreadCount > 9 ? "9+" : unreadCount}
            size={20}
            disabled={unreadCount === 0}
            color="red"
            processing={newNotificationCount > 0}
          >
            <ActionIcon
              variant="subtle"
              size="lg"
              radius="md"
              onClick={handleToggle}
              color={unreadCount > 0 ? "blue" : "gray"}
            >
              <Bell size={20} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown p={0}>
        <Paper>
          {/* Header */}
          <Group position="apart" p="md" pb="xs">
            <Title order={4}>Notifications</Title>
            <Group spacing="xs">
              <Tooltip label={soundEnabled ? "Disable sound" : "Enable sound"}>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  color={soundEnabled ? "blue" : "gray"}
                >
                  {soundEnabled ? (
                    <Volume size={16} />
                  ) : (
                    <VolumeOff size={16} />
                  )}
                </ActionIcon>
              </Tooltip>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setOpened1(false)}
                color="gray"
              >
                <X size={16} />
              </ActionIcon>
            </Group>
          </Group>

          <Divider />

          {/* Filters */}
          <Group position="apart" p="md" py="sm">
            <Group spacing="xs">
              <Button
                variant={filter === "all" ? "filled" : "subtle"}
                size="xs"
                radius="xl"
                onClick={() => setFilter("all")}
              >
                All
              </Button>
              <Button
                variant={filter === "unread" ? "filled" : "subtle"}
                size="xs"
                radius="xl"
                onClick={() => setFilter("unread")}
              >
                Unread
                {unreadCount > 0 && (
                  <Badge size="xs" variant="filled" ml="xs">
                    {unreadCount}
                  </Badge>
                )}
              </Button>
              <Button
                variant={filter === "urgent" ? "filled" : "subtle"}
                size="xs"
                radius="xl"
                color="red"
                onClick={() => setFilter("urgent")}
              >
                Urgent
              </Button>
              {unreadCount > 0 && (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={markAllAsRead}
                  leftIcon={<Check size={14} />}
                >
                  Mark all read
                </Button>
              )}
            </Group>
          </Group>

          <Divider />

          {/* Notifications List */}
          <ScrollArea style={{ height: 320 }}>
            {filteredNotifications.length === 0 ? (
              <Center p="xl">
                <Stack align="center" spacing="sm">
                  <BellOff size={48} color="var(--mantine-color-gray-4)" />
                  <Text color="dimmed" size="sm">
                    No notifications
                  </Text>
                </Stack>
              </Center>
            ) : (
              <Stack spacing={0}>
                {filteredNotifications.map((notification, index) => {
                  return (
                    <div key={notification.id}>
                      <UnstyledButton
                        sx={{
                          width: "100%",
                          padding: "var(--mantine-spacing-md)",
                          backgroundColor: !notification.read
                            ? "var(--mantine-color-blue-0)"
                            : undefined,
                          borderLeft: !notification.read
                            ? "4px solid var(--mantine-color-blue-5)"
                            : undefined,
                          transition: "background-color 0.2s",
                          "&:hover": {
                            backgroundColor: "var(--mantine-color-gray-0)",
                          },
                        }}
                        onClick={() => {
                          markAsRead(notification.id);
                          navigate(
                            `/projects/${notification.projectId}?selectedIssue=${notification.issueId}`
                          );
                        }}
                      >
                        <Group align="flex-start" spacing="sm">
                          {/* Avatar or Icon */}
                          <div style={{ flexShrink: 0 }}>
                            {notification.avatar ? (
                              <Avatar
                                size="sm"
                                radius="xl"
                                color={notification.color}
                              >
                                {notification.avatar}
                              </Avatar>
                            ) : (
                              <Avatar
                                size="sm"
                                radius="xl"
                                color={getPriorityColor(notification.priority)}
                              >
                                {getNotificationIcon(
                                  notification.type,
                                  notification.color
                                )}
                              </Avatar>
                            )}
                          </div>

                          {/* Content */}
                          <div style={{ flex: "1", minWidth: 0 }}>
                            <Group
                              position="apart"
                              align="flex-start"
                              spacing="xs"
                            >
                              <Text size="sm" weight={500} lineClamp={1}>
                                {notification.title}
                              </Text>
                              <ActionIcon
                                variant="subtle"
                                size="xs"
                                color="gray"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  removeNotification(notification.id);
                                }}
                              >
                                <X size={12} />
                              </ActionIcon>
                            </Group>

                            <Text size="xs" color="dimmed" mt={2} lineClamp={2}>
                              {notification.message}
                            </Text>

                            <Group position="apart" mt="xs">
                              <Text
                                size="xs"
                                color="dimmed"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {notification.project}
                              </Text>
                              <Group spacing="xs">
                                <Badge
                                  size="xs"
                                  variant="dot"
                                  color={getPriorityColor(
                                    notification.priority
                                  )}
                                >
                                  {notification.priority}
                                </Badge>
                                <Text size="xs" color="dimmed">
                                  {getTimeAgo(notification.timestamp)}
                                </Text>
                              </Group>
                            </Group>
                          </div>
                        </Group>
                      </UnstyledButton>
                      {index < filteredNotifications.length - 1 && <Divider />}
                    </div>
                  );
                })}
              </Stack>
            )}
          </ScrollArea>

          <Divider />

          {/* Footer */}
          <Center p="sm">
            <Button
              variant="subtle"
              size="sm"
              fullWidth
              onClick={() => {
                navigate(`/notifications`);
              }}
            >
              View all notifications
            </Button>
          </Center>
        </Paper>
      </Menu.Dropdown>
    </Menu>
  );
}

export default Notifications;
